import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ProductState } from "../../../types";
import { axiosObj } from "../../../../config/apiClients";
import { ProductPayload } from "../../../../utils/types";

const initialState: ProductState = {
  isLoading: false,
  isProductLoading: false,
  products: [],
  product: null,
  pagination: {
    totalItems: 0,
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false
  }
}

export const getAllProducts = createAsyncThunk(
  "/esouq/product/getAll",
  async ({ paramPage, paramLimit, search, productType, weightCategory, weight, karat, shapeType, shapeSubType }: { paramPage: number; paramLimit: number, search?: string, productType?: string, weightCategory?: string, weight?: string, karat?: string, shapeType?: string, shapeSubType?: string }, { rejectWithValue }) => {
    try {

      const query = new URLSearchParams();
      query.set("paramPage", String(paramPage));
      query.set("paramLimit", String(paramLimit));
      if (search) query.set("search", search);
      if (productType) query.set("productType", productType);
      if (weightCategory) query.set("weightCategory", weightCategory);
      if (weight) query.set("weight", weight);
      if (karat) query.set("karat", karat);
      if (shapeType) query.set("shapeType", shapeType);
      if (shapeSubType) query.set("shapeSubType", shapeSubType);

      const response = await axiosObj.get(
        `/esouq/product/getAll?${query.toString()}`,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Products' });
    }
  }
);

export const getProduct = createAsyncThunk(
  "/esouq/product/getById",
  async (productId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/esouq/product/getById', { productId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Product' });
    }
  }
)

export const createProduct = createAsyncThunk(
  "/esouq/product/add",
  async (product: ProductPayload, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/esouq/product/create', product);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Add Product' });
    }
  }
)

export const updateProduct = createAsyncThunk(
  "/esouq/product/update",
  async (product: ProductPayload, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch('/esouq/product/update', product);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Update Product' });
    }
  }
)

export const deleteProduct = createAsyncThunk(
  "/esouq/product/delete",
  async (productId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.delete('/esouq/product/delete', { data: { productId } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Delete Product' });
    }
  }
)

export const updatePremiumDiscount = createAsyncThunk(
  "/esouq/product/premium-discount",
  async ({ premiumDiscountInDollar, productSymbol }: { premiumDiscountInDollar: number | string, productSymbol: string }, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch('/esouq/product/premium-discounts/bulk', { premiumDiscountInDollar, productSymbol });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Update Premium Discount' });
    }
  }
)

const productSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    removeProduct: (state, action) => {
      const productId = action.payload;
      state.products = state.products.filter((product) => product._id !== productId);
    },
    setProduct: (state, action) => {
      state.product = action.payload;
    },
    clearSelectedProduct: (state) => {
      state.product = null;
      state.isProductLoading = false;
    },
    resetProducts(state) {
      state.products = [];
      state.pagination = {
        totalItems: 0,
        currentPage: 1,
        hasNextPage: false,
        totalPages: 1,
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getAllProducts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.products = action.payload.payload.allProducts;
        state.pagination = {
          totalItems: action.payload.payload.totalItems,
          currentPage: action.payload.payload.page,
          totalPages: action.payload.payload.totalPages,
          hasNextPage: action.payload.payload.hasNextPage
        };
      })
      .addCase(getAllProducts.rejected, (state) => {
        state.isLoading = false;
        state.products = [];
      })
      .addCase(getProduct.pending, (state) => {
        state.isProductLoading = true;
      })
      .addCase(getProduct.fulfilled, (state, action) => {
        state.isProductLoading = false;
        state.product = action.payload.payload;
      })
      .addCase(getProduct.rejected, (state) => {
        state.isProductLoading = false;
        state.product = null;
      })
  },
});

export const { setProduct, removeProduct, resetProducts, clearSelectedProduct } = productSlice.actions;

export default productSlice.reducer;